'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LottieRender from './LottieRender';
import Icon from '@/components/ui/AppIcon';

gsap.registerPlugin(ScrollTrigger);

const MobileCultureProcess = () => {
    const containerRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        // Staggered Steps Animation
        gsap.from(".process-step", {
            y: 30,
            opacity: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: "power2.out",
            scrollTrigger: {
                trigger: ".steps-container",
                start: "top 75%",
                toggleActions: "play none none reverse"
            }
        });

        // Connection Line Grow
        gsap.from(".connect-line", {
            height: 0,
            duration: 1.5,
            ease: "power2.inOut",
            scrollTrigger: {
                trigger: ".steps-container",
                start: "top 70%",
            }
        });

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="py-20 bg-slate-50 overflow-hidden">
            <div className="px-6">

                {/* --- Part 1: Culture --- */}
                <div className="mb-20 relative">
                    <div className="text-center mb-8">
                        <h2 className="text-4xl font-black text-slate-900 mb-4">
                            Cultivating <br />
                            <span className="text-indigo-600">Growth</span>
                        </h2>
                        <p className="text-slate-600 leading-relaxed font-medium">
                            Our culture is built on continuous learning. A happy, challenged team delivers the most innovative solutions.
                        </p>
                    </div>

                    {/* Culture Card */}
                    <div className="h-64 bg-white rounded-[2.5rem] shadow-xl shadow-indigo-100 overflow-hidden relative border border-slate-100">
                        {/* Decorative Gradient Blob */}
                        <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl -mr-16 -mt-16" />

                        <div className="w-full h-full p-6">
                            <LottieRender animationPath="/animations/culture_embedded.json" />
                        </div>
                    </div>
                </div>


                {/* --- Part 2: Precision Process --- */}
                <div className="steps-container relative">
                    <div className="text-center mb-10">
                        <h2 className="text-4xl font-black text-slate-900 mb-2">
                            Precision <span className="text-indigo-600">Process</span>
                        </h2>
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">
                            From Ideation to Impact
                        </p>
                    </div>

                    {/* Vertical Connected Steps */}
                    <div className="relative pl-4 space-y-6">
                        {/* The Connection Line */}
                        <div className="connect-line absolute left-[39px] top-6 bottom-6 w-[3px] bg-gradient-to-b from-indigo-500 via-violet-500 to-indigo-100 rounded-full origin-top" />

                        {[
                            { title: "Discovery", desc: "Deep dive & strategy.", icon: "ChatBubbleLeftRightIcon" },
                            { title: "Architecture", desc: "Planning the core.", icon: "PencilSquareIcon" },
                            { title: "Development", desc: "Building with scale.", icon: "CodeBracketIcon" },
                            { title: "Optimization", desc: "Refining for speed.", icon: "RocketLaunchIcon" }
                        ].map((step, i) => (
                            <div key={i} className="process-step relative flex items-center z-10">
                                {/* Icon Node */}
                                <div className="w-12 h-12 rounded-2xl bg-white border-2 border-indigo-100 shadow-md flex items-center justify-center mr-5 shrink-0 z-20">
                                    <Icon name={step.icon} className="w-6 h-6 text-indigo-600" />
                                </div>

                                {/* Step Content */}
                                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex-grow">
                                    <h3 className="font-bold text-slate-900 text-lg mb-1">{step.title}</h3>
                                    <p className="text-slate-500 text-sm font-medium">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>

            </div>
        </section>
    );
};

export default MobileCultureProcess;
